import { useCallback, useState } from "react"

import { useGoodsReceiptPalletLabelPrinting } from "./useGoodsReceiptPalletLabelPrinting"

import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { useDeleteGoodsReceiptPallet } from "@/screens/WarehouseReceiving/hooks/useDeleteGoodsReceiptPallet"
import type { GoodsReceiptPalletActionState } from "@/screens/WarehouseReceiving/types"
import { GoodsReceiptPalletActionType } from "@/screens/WarehouseReceiving/types"

interface UseGoodsReceiptPalletActionsProps {
  onRefetch?: () => void
}

interface UseGoodsReceiptPalletActionsReturn {
  actionState: GoodsReceiptPalletActionState
  openActionsMenuId: string | null
  handleActionsMenuOpen: (palletId: string) => void
  handleActionsMenuClose: () => void
  handleCreate: () => void
  handleLpnEdit: (pallet: GoodsReceiptPallet) => void
  handleDelete: (pallet: GoodsReceiptPallet) => void
  handleDeleteConfirm: () => Promise<void>
  handleDestinationOverride: (pallet: GoodsReceiptPallet) => void
  handleQrPrint: (pallet: GoodsReceiptPallet) => void
  handleQrPrintAll: (pallets: GoodsReceiptPallet[]) => Promise<void>
  handleActionSuccess: () => void
  handleActionClose: () => void
}

export const useGoodsReceiptPalletActions = ({
  onRefetch,
}: UseGoodsReceiptPalletActionsProps = {}): UseGoodsReceiptPalletActionsReturn => {
  const [actionState, setActionState] = useState<GoodsReceiptPalletActionState>(null)
  const [openActionsMenuId, setOpenActionsMenuId] = useState<string | null>(null)
  const { printPalletLabel } = useGoodsReceiptPalletLabelPrinting()
  const { deletePallet } = useDeleteGoodsReceiptPallet({ onSuccess: onRefetch })

  const handleActionsMenuOpen = useCallback((palletId: string) => {
    setOpenActionsMenuId(palletId)
  }, [])

  const handleActionsMenuClose = useCallback(() => {
    setOpenActionsMenuId(null)
  }, [])

  const handleQrPrint = useCallback(
    (pallet: GoodsReceiptPallet) => {
      printPalletLabel(pallet.referenceId)
    },
    [printPalletLabel]
  )

  const handleQrPrintAll = useCallback(
    async (pallets: GoodsReceiptPallet[]) => {
      const sortedPallets = [...pallets].sort((a, b) => {
        return a.referenceId - b.referenceId
      })

      for (const pallet of sortedPallets) {
        const success = await printPalletLabel(pallet.referenceId)
        if (!success) {
          return
        }
      }
    },
    [printPalletLabel]
  )

  const handleActionClose = useCallback(() => {
    setActionState(null)
  }, [])

  const handleLpnEdit = useCallback((pallet: GoodsReceiptPallet) => {
    setActionState({ type: GoodsReceiptPalletActionType.EditLpn, pallet })
  }, [])

  const handleDelete = useCallback((pallet: GoodsReceiptPallet) => {
    setActionState({ type: GoodsReceiptPalletActionType.Delete, pallet })
  }, [])

  const handleDestinationOverride = useCallback((pallet: GoodsReceiptPallet) => {
    setActionState({ type: GoodsReceiptPalletActionType.EditDestination, pallet })
  }, [])

  const handleCreate = useCallback(() => {
    setActionState({ type: GoodsReceiptPalletActionType.Create })
  }, [])

  const handleActionSuccess = useCallback(() => {
    setActionState(null)
    onRefetch?.()
  }, [onRefetch])

  const handleDeleteConfirm = useCallback(async () => {
    if (actionState?.type !== GoodsReceiptPalletActionType.Delete) {
      return
    }

    await deletePallet(actionState.pallet.goodsReceiptPalletId)
    handleActionSuccess()
  }, [actionState, deletePallet, handleActionSuccess])

  return {
    actionState,
    openActionsMenuId,
    handleActionsMenuOpen,
    handleActionsMenuClose,
    handleCreate,
    handleLpnEdit,
    handleDelete,
    handleDeleteConfirm,
    handleDestinationOverride,
    handleQrPrint,
    handleQrPrintAll,
    handleActionSuccess,
    handleActionClose,
  }
}
